/**
 * AYUNEL 商品页三标签 —— blocks/ayunel-pdp-tabs.liquid 的行为层(新房 Horizon 4.1.4)。2026-09-16;同日 v2 加手风琴模式。
 *
 * 只管六件事:选哪个标签、细线停在哪、内容怎么换、手机上哪几块打开着、过期的活动说明藏起来、
 * 「商品のポイント」的もっと見る / 閉じる(2026-09-30)。文案全在 Liquid,这里不拼一个字(两个按钮文案都在 Liquid,这里只切 aria-expanded)。
 * 以 <script type="module"> 引入(与 domestic-line / 套装块同姿势)。
 *
 * 两种模式、同一份 DOM(智鹏 2026-09-16 v2:手机照 SOÉJU 改手风琴):
 * - 标签模式(≥750px):下面「死约」的全部。
 * - 手风琴模式(≤749px,matchMedia 判、change 事件切):标签行 CSS 藏起;每块前一个 .ayunel-pdp-tabs__acc 按钮
 *   (aria-expanded / aria-controls),各块**互相独立**开合(可同时开多块),初值全部收起(智鹏 9/16 晚:第一项也不默认展开);
 *   ⚖️ 2026-09-30 例外:面板带 data-ayn-open-on-mobile 的(= 商品のポイント,有详情图才有)初值展开(智鹏 9/30:「手机端就是不能默认关掉」)。
 *   面板 role=region、aria-labelledby 指向按钮;打开复用 320ms 淡入,关闭立刻 hidden。
 *   两种模式共用一个「客人最后看的块」:手风琴里打开某块 = 标签行也选中它;标签里选中某块 = 手风琴里它记为开着。
 *   手风琴 → 标签(转屏 / 拉宽):只显示最后看的那块;标签 → 手风琴:按各块的开合数组(9/16 复审:两向切换都不让正在读的内容跳走)。
 *
 * 死约:
 * - 无障碍按 WAI-ARIA tabs:role=tab / aria-selected / aria-controls,roving tabindex(只有选中的那个 tabindex=0),
 *   ← → 在标签间移动并直接切换,Home / End 到头尾。
 * - **切换时新旧内容立刻换过来**(旧的马上 hidden、新的马上显示),只给新的做 320ms 淡入(手册入场档)。
 *   ⚖️ 2026-09-16 代码审查改:原先照 SOÉJU 先淡出 220ms 再换 —— 这 220ms 里键盘用户按 Tab 会进到旧内容、
 *   随后旧内容被藏、焦点掉到 <body>,读屏读到的也是旧内容;快速连点还会留一块透明占位。无障碍优先,淡出去掉。
 * - 细线 = 宽 1px 的条,按选中标签 offsetLeft / offsetWidth 写 translateX + scaleX;首次定位不带动画,之后切换才滑;
 *   标签行尺寸变化(字体加载完、转屏、放大字号)时重新定位。标签行放不下时可横向滑动,切换时把选中的滑进可视区。
 * - prefers-reduced-motion:细线与淡入都不播。
 * - 活动期说明(Liquid 按渲染时刻判断,商品页有整页缓存 ⇒ 9/22 00:00 JST 后可能还挂在缓存里):
 *   容器带 data-ayn-camp-end(ISO 时刻),过了就在客户端藏起来。守卫钉住这个时刻与活动块常量一致。
 * - 宿主由 Liquid 双挂 morph skip,换色码时整棵子树不被服务端 HTML 覆盖,选中状态留在客人手里;
 *   连回 DOM 时按 aria-selected 归一一次(防中途被移出留下半截状态)。
 * 守卫:tests/test_theme_pdp_tabs.py(结构 / 文案 / 真渲染;本文件只做字符串级契约)+ 08-themes/pdp-tabs-src/pdp_tabs_walk_dev.py(DEV 真浏览器行为)。
 */
const TAG = 'ayunel-pdp-tabs';
const ENTER_MS = 320;

class AyunelPdpTabs extends HTMLElement {
  #controller;
  #generation = 0;
  #timer = 0;
  #resizeObserver;
  #mq;
  #expanded = [];
  #pics = [];
  #picsObserver;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.list = this.querySelector('[role="tablist"]');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = this.tabs.map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls') || '')}`));
    this.toggles = [...this.querySelectorAll('.ayunel-pdp-tabs__acc')];
    this.ink = this.querySelector('.ayunel-pdp-tabs__ink');
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index, { focus: false }), { signal });
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index), { signal });
    });
    this.toggles.forEach((toggle, index) => {
      toggle.addEventListener('click', () => this.toggle(index), { signal });
    });
    this.querySelectorAll('.ayunel-pdp-tabs__more-btn').forEach((button) => {
      button.addEventListener('click', () => this.#toggleMore(button), { signal });
    });
    // 图少时整组本来就不到露出高度 ⇒ 不截、不出按钮;面板藏着时量不到(高 0),显示出来 / 宽度变化时 ResizeObserver 再量
    this.#pics = [...this.querySelectorAll('.ayunel-pdp-tabs__pics')];
    if (typeof ResizeObserver === 'function' && this.#pics.length) {
      this.#picsObserver = new ResizeObserver(() => this.#fitMore());
      this.#pics.forEach((list) => this.#picsObserver.observe(list));
    }
    // 手机默认全收起(智鹏 2026-09-16 晚:第一项也不默认展开,照 SOÉJU);只有商品のポイント 例外默认展开(智鹏 2026-09-30)
    this.#expanded = this.panels.map((panel) => !!panel?.hasAttribute('data-ayn-open-on-mobile'));
    this.#mq = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 749px)') : null;
    this.#mq?.addEventListener('change', () => this.#applyMode(), { signal });
    this.#hideExpiredCampaign();
    if (typeof ResizeObserver === 'function' && this.list) {
      this.#resizeObserver = new ResizeObserver(() => this.#placeInk(false));
      this.#resizeObserver.observe(this.list);
    }
    document.fonts?.ready?.then(() => this.#placeInk(false));
    this.#applyMode();
  }

  disconnectedCallback() {
    this.#generation += 1;
    clearTimeout(this.#timer);
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#picsObserver?.disconnect();
  }

  get selectedIndex() {
    const i = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    return i < 0 ? 0 : i;
  }

  /** 手机(≤749px)= 手风琴;没有 matchMedia(老环境)就一直是标签。 */
  get accordion() {
    return !!this.#mq?.matches;
  }

  /** 手风琴:翻转第 index 块的开合,其余块不动。 */
  toggle(index) {
    const panel = this.panels[index];
    const button = this.toggles[index];
    if (!panel || !button) return;
    const open = !this.#expanded[index];
    this.#expanded[index] = open;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) this.#mark(index);
    if (!open) {
      panel.classList.remove('is-entering', 'is-entered');
      panel.hidden = true;
      return;
    }
    panel.hidden = false;
    this.#fadeIn(panel);
  }

  /** 按当前模式把面板的 hidden / role / aria-labelledby、按钮的 aria-expanded 全部对齐(连回 DOM、转屏、拉宽都走这里)。 */
  #applyMode() {
    const acc = this.accordion && this.toggles.length === this.panels.length;
    this.panels.forEach((panel, i) => {
      if (!panel) return;
      const button = this.toggles[i];
      if (acc) {
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', button.id);
      } else {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', this.tabs[i].id);
      }
      if (button) button.setAttribute('aria-expanded', this.#expanded[i] ? 'true' : 'false');
    });
    if (acc) {
      this.#generation += 1;
      clearTimeout(this.#timer);
      this.panels.forEach((panel, i) => {
        if (!panel) return;
        panel.classList.remove('is-entering', 'is-entered');
        panel.hidden = !this.#expanded[i];
      });
      return;
    }
    this.#settle(this.panels[this.selectedIndex]);
    this.#placeInk(false);
  }

  /** 标签行的选中标记(aria-selected + roving tabindex);标签切换与手风琴打开都走这里,两种模式记的是同一个「客人最后看的块」。 */
  #mark(index) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.setAttribute('tabindex', on ? '0' : '-1');
    });
  }

  select(index, { focus = true } = {}) {
    if (index < 0 || index >= this.tabs.length) return;
    const shown = this.panels.find((panel) => panel && !panel.hidden);
    this.#mark(index);
    this.#expanded[index] = true;
    if (focus) this.tabs[index].focus();
    this.#scrollTabIntoView(this.tabs[index]);
    this.#placeInk(true);
    const to = this.panels[index];
    if (!to || shown === to) return;
    this.#show(to);
  }

  #onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.select(next, { focus: true });
  }

  #reducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** 只留 target 显示,其余立刻 hidden,清掉所有动画 class。 */
  #settle(target) {
    this.panels.forEach((panel) => {
      if (!panel) return;
      panel.classList.remove('is-entering', 'is-entered');
      panel.hidden = panel !== target;
    });
  }

  #show(to) {
    this.#settle(to);
    this.#fadeIn(to);
  }

  /** 320ms 淡入(标签切换与手风琴打开共用);reduced-motion 直接显示。 */
  #fadeIn(to) {
    const gen = (this.#generation += 1);
    clearTimeout(this.#timer);
    if (this.#reducedMotion()) return;
    to.classList.add('is-entering');
    void to.offsetWidth;
    to.classList.add('is-entered');
    to.classList.remove('is-entering');
    this.#timer = setTimeout(() => {
      if (gen !== this.#generation) return;
      to.classList.remove('is-entered');
    }, ENTER_MS + 20);
  }

  #scrollTabIntoView(tab) {
    const list = this.list;
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const left = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
    list.scrollTo({ left: Math.max(0, left), behavior: this.#reducedMotion() ? 'auto' : 'smooth' });
  }

  #placeInk(animate) {
    const ink = this.ink;
    const tab = this.tabs[this.selectedIndex];
    if (!ink || !tab) return;
    const width = tab.offsetWidth;
    if (!width) return;
    if (animate && !this.#reducedMotion()) ink.classList.add('is-animated');
    else ink.classList.remove('is-animated');
    ink.style.transform = `translateX(${tab.offsetLeft}px) scaleX(${width})`;
    ink.classList.add('is-ready');
  }

  /** 商品のポイント:先露一截 ⇄ 全部展开。
   *  展开:焦点移到图片区顶(键盘用户不会被带到页面最底下的按钮上)。
   *  收起:若图片区的顶已被吸顶页头盖住或滑出屏幕,滚回这一格的标题(电脑 = 标签行,手机 = 手风琴标题;CSS 给了 scroll-margin-top 让开页头)。
   *  ⚖️ 9/30 复审:原先把图片区本身滚到 y=0,被吸顶页头盖住 60–80px、看不到在哪一格;reduced-motion 要传 'instant'
   *  (主题给滚动容器设了 scroll-behavior:smooth,传 'auto' 仍会平滑滚)。 */
  #toggleMore(button) {
    const box = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls') || '')}`);
    if (!box) return;
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    box.classList.toggle('is-open', open);
    if (open) {
      box.focus({ preventScroll: true });
      return;
    }
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    if (box.getBoundingClientRect().top >= header) return;
    const panel = box.closest('.ayunel-pdp-tabs__panel');
    const i = this.panels.indexOf(panel);
    const target = this.accordion ? this.toggles[i]?.parentElement : this.list;
    (target || box).scrollIntoView({ block: 'start', behavior: this.#reducedMotion() ? 'instant' : 'smooth' });
  }

  /** 图少:图片列表不超过露出高度(--ayn-more-h)+40 就不截。藏着的面板(高 0)跳过,等显示出来再量。 */
  #fitMore() {
    this.#pics.forEach((list) => {
      const box = list.closest('.ayunel-pdp-tabs__more');
      const h = list.offsetHeight;
      if (!box || !h) return;
      const limit = parseFloat(getComputedStyle(box).getPropertyValue('--ayn-more-h')) || 0;
      box.classList.toggle('is-short', h <= limit + 40);
    });
  }

  #hideExpiredCampaign() {
    this.querySelectorAll('[data-ayn-camp-end]').forEach((el) => {
      const end = Date.parse(el.getAttribute('data-ayn-camp-end') || '');
      if (Number.isFinite(end) && Date.now() > end) el.hidden = true;
    });
  }
}

if (!customElements.get(TAG)) customElements.define(TAG, AyunelPdpTabs);
